import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { ProductSummary, RequirementFieldWithChildren } from '../product-requirements-config.dto';

interface Props {
    field: RequirementFieldWithChildren;
    /** Products this field is currently assigned to (their edges are removed with it). */
    assignedProducts: ProductSummary[];
    saving?: boolean;
    onConfirm: () => void;
    onClose: () => void;
}

/**
 * Delete confirmation with the blast radius spelled out: which products stop
 * collecting the field, and — mirroring the backend's 409 guard — that a LIST
 * parent with sub-fields cannot be deleted until the children are gone.
 * Collected customer answers are never deleted (they live in data-products,
 * keyed by code) — the field just stops being asked for.
 */
export default function ConfirmDeleteFieldDialog({ field, assignedProducts, saving, onConfirm, onClose }: Props) {
    const { t } = useTranslation();
    const hasChildren = field.subFields.length > 0;

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('Delete field "{{label}}"?', { label: field.label })}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Typography variant="body2">
                        {field.label}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                            {field.code}
                        </Typography>
                    </Typography>

                    {hasChildren && (
                        <Alert severity="warning">
                            {t(
                                'This list has {{count}} sub-fields. Delete the sub-fields first — the backend refuses to delete a parent with children.',
                                { count: field.subFields.length },
                            )}
                        </Alert>
                    )}

                    {assignedProducts.length > 0 ? (
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                {t('This field is currently collected for:')}
                            </Typography>
                            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                                {assignedProducts.map((p) => (
                                    <Chip key={p.id} size="small" color="primary" label={p.sku} />
                                ))}
                            </Stack>
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            {t('This field is not assigned to any product.')}
                        </Typography>
                    )}

                    <Typography variant="caption" color="text.secondary">
                        {t('Customer answers already collected are kept — the question just stops being asked.')}
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('Cancel')}</Button>
                <Button variant="contained" color="error" disabled={saving || hasChildren} onClick={onConfirm}>
                    {t('Delete field')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
